import { UnstyledButton, Group, Box, Center, Text } from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { extractAudioBytes, extractAudioDurationSeconds, parseAudioAssetPayload } from '@shared/utils/audio';
import { transcodeFlutterAudioToPlayableWav } from '@shared/utils/audioTranscode';

type AudioMessageBubbleProps = {
  msgData?: Uint8Array;
  isMine: boolean;
};

function formatDuration(seconds: number) {
  if (seconds < 60) {
    return `${seconds}"`;
  }
  const minutes = Math.floor(seconds / 60);
  const remain = seconds % 60;
  return `${minutes}'${String(remain).padStart(2, '0')}"`;
}

function bubbleWidth(duration: number) {
  const minWidth = 122;
  const maxWidth = 220;
  const clampedDuration = Math.max(1, Math.min(duration, 60));
  return minWidth + ((maxWidth - minWidth) * clampedDuration) / 60;
}

async function fetchAudioBytes(url: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`音频下载失败: ${response.status}`);
  }
  return new Uint8Array(await response.arrayBuffer());
}

export function AudioMessageBubble({ msgData, isMine }: AudioMessageBubbleProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [playableBytes, setPlayableBytes] = useState<Uint8Array | null>(null);
  const preparingRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const audioAsset = useMemo(() => parseAudioAssetPayload(msgData), [msgData]);
  const audioBytes = useMemo(() => extractAudioBytes(msgData), [msgData]);
  const duration = useMemo(() => extractAudioDurationSeconds(msgData), [msgData]);

  useEffect(() => {
    return () => {
      if (sourceRef.current) {
        try {
          sourceRef.current.stop();
        } catch { }
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
    };
  }, [audioAsset?.url, audioBytes]);

  useEffect(() => {
    return () => {
      if (sourceRef.current) {
        try {
          sourceRef.current.stop();
        } catch { }
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (audioContextRef.current) {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  const handleTogglePlay = async () => {
    if (isPreparing || preparingRef.current) return;
    if (isPlaying) {
      if (sourceRef.current) {
        try {
          sourceRef.current.stop();
        } catch { }
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      setIsPlaying(false);
      return;
    }
    preparingRef.current = true;
    setIsPlaying(true);
    setIsPreparing(true);
    try {
      let audioToPlay = playableBytes;
      if (!audioToPlay) {
        if (audioBytes.length === 0 && !audioAsset?.url) return;
        const sourceBytes = audioAsset?.url
          ? await fetchAudioBytes(audioAsset.url)
          : audioBytes;
        const { bytes } = await transcodeFlutterAudioToPlayableWav(sourceBytes);
        audioToPlay = bytes;
        setPlayableBytes(bytes);
        console.info('[voice] playback-transcode-finished', { bytes: bytes.length });
      }

      const AudioContextCtor = window.AudioContext ?? window.webkitAudioContext;
      if (!AudioContextCtor) {
        throw new Error('当前浏览器不支持音频播放上下文');
      }
      const audioContext = audioContextRef.current ?? new AudioContextCtor();
      audioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const arrayBuffer = audioToPlay.buffer.slice(
        audioToPlay.byteOffset,
        audioToPlay.byteOffset + audioToPlay.byteLength,
      );
      const decoded = await audioContext.decodeAudioData(arrayBuffer);
      const source = audioContext.createBufferSource();
      source.buffer = decoded;
      source.connect(audioContext.destination);
      source.onended = () => {
        if (sourceRef.current === source) {
          sourceRef.current = null;
        }
        setIsPlaying(false);
      };
      sourceRef.current = source;
      source.start(0);
      console.info('[voice] playback-started', { duration: decoded.duration });
    } catch (error) {
      console.error('[voice] playback-start-failed', error);
      setIsPlaying(false);
    } finally {
      preparingRef.current = false;
      setIsPreparing(false);
    }
  };

  const barCount = Math.max(7, Math.min(Math.round(duration * 1.5), 18));
  const waveformHeights = [
    0.28, 0.48, 0.76, 0.42, 1, 0.62, 0.34, 0.7, 0.46,
    0.86, 0.38, 0.58, 0.3, 0.74, 0.5, 0.9, 0.4, 0.64,
  ];

  return (
    <UnstyledButton
      onClick={() => void handleTogglePlay()}
      disabled={isPreparing || (audioBytes.length === 0 && !audioAsset?.url)}
      style={{
        width: `${bubbleWidth(duration)}px`,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 14px',
        borderRadius: 0,
        backgroundColor: 'transparent',
        transition: 'all 0.2s ease',
      }}
    >
      <Center w={20} h={20} style={{ flexShrink: 0 }}>
        <Text size="xs" fw={900}>
          {isPlaying ? '⏸' : '▶'}
        </Text>
      </Center>
      <Group gap={3} align="center" style={{ flex: 1 }}>
        {Array.from({ length: barCount }).map((_, index) => {
          const heightFactor = waveformHeights[index % waveformHeights.length];
          return (
            <Box
              key={index}
              className="locs-audio-wave-bar"
              bg={isMine ? 'rgba(255,255,255,0.9)' : 'var(--locs-text)'}
              w={2}
              style={{
                height: 18 * heightFactor,
                borderRadius: 999,
                animation: isPlaying ? 'locs-audio-wave 520ms ease-in-out infinite alternate' : 'none',
                animationDelay: isPlaying ? `${(index % 5) * -90}ms` : '0ms',
                opacity: isPlaying ? 1 : 0.72,
              }}
            />
          );
        })}
      </Group>
      <Text size="xs" fw={700} style={{ flexShrink: 0 }}>
        {formatDuration(duration)}
      </Text>
    </UnstyledButton>
  );
}
